/**
 * OrkexraLogo — official brand logo component.
 * Patch 36.1 · Orkexra Brand Identity.
 *
 * Three canonical variants, driven by the same source PNG assets in
 * ``/public/brand/``. Do NOT approximate the mark by drawing SVG paths
 * by hand — the assets in /brand were extracted directly from the
 * official Orkexra brand board and are the canonical source of truth.
 *
 *   • variant="mark"     — icon mark only. Best at very small sizes
 *                          (favicons, avatars, dense navigation).
 *   • variant="wordmark" — compact horizontal lockup (mark + wordmark
 *                          side-by-side). Use in the topbar / sidebar.
 *   • variant="lockup"   — full centred vertical lockup with wordmark,
 *                          tagline and "Powered by Akinex" attribution.
 *                          Use on welcome, login, demo hero and splash.
 */
import type { CSSProperties } from "react";

export type OrkexraLogoVariant = "mark" | "wordmark" | "lockup";

export interface OrkexraLogoProps {
  variant?: OrkexraLogoVariant;
  /** Height in pixels for the mark; the wordmark scales accordingly. */
  size?: number;
  className?: string;
  /** Accessible label (defaults to "Orkexra"). */
  ariaLabel?: string;
  /** Set false to render as decorative art only (aria-hidden). */
  labelled?: boolean;
  style?: CSSProperties;
}

/**
 * PATH is served from /public/brand — never bundle these PNGs into
 * the JS build. This keeps the topbar TTI fast and the mark cached
 * separately from the app bundle.
 */
const MARK_URL = "/brand/orkexra-mark.png";
const LOCKUP_URL = "/brand/orkexra-lockup.png";

export function OrkexraLogo({
  variant = "wordmark",
  size = 28,
  className,
  ariaLabel = "Orkexra",
  labelled = true,
  style,
}: OrkexraLogoProps): JSX.Element {
  const commonProps = labelled
    ? { role: "img" as const, "aria-label": ariaLabel }
    : { "aria-hidden": true };

  // NOTE (Patch 36.1 fix): NEVER hard-code `display` in inline style —
  // inline styles beat Tailwind utility classes so callers cannot use
  // `hidden`/`sm:hidden` etc to switch variants responsively. Instead
  // we apply a default `.inline-flex` class that any caller-supplied
  // display class overrides through Tailwind's cascade.
  const mergedClass = ["inline-flex", className].filter(Boolean).join(" ");

  if (variant === "mark") {
    return (
      <span
        {...commonProps}
        className={mergedClass}
        style={style}
      >
        <img
          src={MARK_URL}
          alt=""
          width={size}
          height={size}
          style={{
            width: size, height: size, display: "block",
            objectFit: "contain",
          }}
          draggable={false}
        />
      </span>
    );
  }

  if (variant === "lockup") {
    return (
      <span
        {...commonProps}
        className={mergedClass}
        style={style}
      >
        <img
          src={LOCKUP_URL}
          alt=""
          height={size}
          style={{
            height: size, width: "auto", display: "block",
            objectFit: "contain",
          }}
          draggable={false}
        />
      </span>
    );
  }

  // wordmark (compact horizontal): mark + typographic wordmark using
  // Space Grotesk with a gradient "X" per the brand board.
  const markSize = size;
  return (
    <span
      {...commonProps}
      className={mergedClass}
      style={{
        alignItems: "center", gap: markSize * 0.32,
        ...style,
      }}
    >
      <img
        src={MARK_URL}
        alt=""
        width={markSize}
        height={markSize}
        style={{
          width: markSize, height: markSize, display: "block",
          objectFit: "contain",
        }}
        draggable={false}
      />
      <span
        aria-hidden
        className="orkexra-wordmark"
        style={{
          fontSize: Math.max(12, Math.round(markSize * 0.62)),
          lineHeight: 1,
        }}
      >
        ORKE<span className="x">X</span>RA
      </span>
    </span>
  );
}

/** Small helper: the canonical Akinex attribution eyebrow, used on
 *  welcome, login, demo hero and the Executive Briefing Card. */
export function AkinexAttribution({
  className,
}: { className?: string }): JSX.Element {
  return (
    <span
      className={`akinex-eyebrow ${className ?? ""}`}
      data-testid="akinex-attribution"
    >
      Powered by Akinex&trade; Adaptive Intelligence
    </span>
  );
}
